import { useModals, Card, ListOption, Skeleton, Stack, Tag, Title } from "@skailer-com/uikit";
import { ApplyRoleSettingsToAnotherRole } from "@/pages/settings/roles/index/widget/ApplyRoleSettingsToAnotherRole.tsx";
import { getRoleDescription, getRoleTitle } from "@/pages/settings/roles/role.types.ts";
import { useRoles } from "@/queries/settings/use-roles.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { PrincipalType, RoleGroup, RoleGroupType, RoleType } from "@/types/role.types.ts";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function RolesList() {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const groups: Record<RoleGroupType, RoleGroup> = {
    [RoleGroupType.base]: {
      title: t("core:roles_basic"),
      description: t("core:roles_basic_description"),
      roles: [],
      type: RoleGroupType.base,
    },
    [RoleGroupType.system]: {
      title: t("core:roles_system"),
      description: t("core:roles_system_description"),
      roles: [],
      type: RoleGroupType.system,
    },
    [RoleGroupType.custom]: {
      title: t("core:roles_custom"),
      description: t("core:roles_custom_description"),
      roles: [],
      type: RoleGroupType.custom,
    },
    [RoleGroupType.service_users]: {
      title: t("core:roles_service_users"),
      description: t("core:roles_service_users_description"),
      roles: [],
      type: RoleGroupType.service_users,
    },
  };
  const { data: roles, isLoading: rolesIsLoading } = useRoles(true, true);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.roles.viewed);
  });

  if (roles) {
    const mapTypeRoleToRoleGroup: Record<RoleType, RoleGroupType> = {
      [RoleType.custom]: RoleGroupType.custom,
      [RoleType.admin]: RoleGroupType.system,
      [RoleType.team_lead]: RoleGroupType.system,
      [RoleType.manager]: RoleGroupType.system,
      [RoleType.myself]: RoleGroupType.base,
      [RoleType.all_employees]: RoleGroupType.base,
    };

    roles.forEach((role) => {
      const groupType =
        role.principal_type === PrincipalType.api_user
          ? RoleGroupType.service_users
          : mapTypeRoleToRoleGroup[role.type];

      groups[groupType].roles.push(role);
    });
  }

  const applySettingsToAnotherRole = (roleId: number) => {
    openModal(ApplyRoleSettingsToAnotherRole, { roleId });
  };

  return (
    <Stack direction={"vertical"} className={"py-6"}>
      {Object.entries(groups).map(([key, group]) => (
        <Stack direction={"vertical"} gap={"md"} key={`group-${key}`}>
          <Title
            header={group.title}
            caption={group.description}
            size={"lg"}
            paddingBottom
            paddingTop
          />

          <Stack
            direction={"horizontal"}
            gap={"md"}
            className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"}
          >
            {rolesIsLoading && <Card isLoading={true} />}

            {group.roles.map((role) => (
              <Card
                header={getRoleTitle(role)}
                caption={getRoleDescription(role)}
                to={`/settings/roles-and-permissions/${role.id}`}
                buttonsSlot={
                  role.is_recalculating ? (
                    <Skeleton className={"h-6 w-22"} />
                  ) : (
                    <Tag>
                      {t(
                        key === RoleGroupType.service_users
                          ? "plurals:service_users_count"
                          : "plurals:employees_count",
                        { count: role.employees_count }
                      )}
                    </Tag>
                  )
                }
                key={role.id}
                menu={
                  <ListOption
                    title={t("core:apply_settings_to_another_role")}
                    onClick={() => {
                      applySettingsToAnotherRole(role.id);
                    }}
                  />
                }
              />
            ))}

            {key == RoleGroupType.custom && (
              <Card
                icon={"fa-plus"}
                header={t("common:create_new")}
                to={`/settings/roles-and-permissions/new`}
                variant={"action"}
              />
            )}

            {key == RoleGroupType.service_users && (
              <Card
                icon={"fa-plus"}
                header={t("common:create_new")}
                to={`/settings/roles-and-permissions/new?principal_type=api_user`}
                variant={"action"}
              />
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
